import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { getGroupsLocalStorageKey } from "../../auth/user-state-service";
import { GroupService } from "../../data-access-layer/groups/group.service";
import { MaiaUploadService } from "../../data-access-layer/maia/maia-upload.service";
import { Group } from "../../model/group/group";
import { SovAiField } from "../../model/maia/sov-upload";

/** A group row rendered in the EXISTING GROUPS panel (parentId set = subgroup). */
interface MaiaGroupRow {
    id: number;
    name: string;
    parentId: number | null;
    count: number;
}

/** One applied facet: ticked text values, or a numeric min/max range (AND across criteria). */
interface FilterCriterion {
    fieldId: string;
    fieldName: string;
    values: string[];
    min: number | null;
    max: number | null;
    label: string;
}

/** A recorded "Add to Group" - the subset (criteria, or all-remaining) promoted on Import. */
interface PendingAssignment {
    groupId: number;
    criteria: FilterCriterion[];
    selectAll: boolean;
    /** Locations this assignment added - stored so counts/progress restore on refresh without re-querying. */
    added: number;
}

const NUMBER_FIELD = "NUMBER_FIELD";
const DATE_FIELD = "DATE_FIELD";
const DAY_MS = 86_400_000;

/** UTC epoch millis -> yyyy-MM-dd for a date input; uploaded dates are stored at UTC midnight. */
function toDateInput(epoch: number | null): string {
    return epoch == null ? "" : new Date(epoch).toISOString().slice(0, 10);
}

export type MaiaGroupView = "grouping" | "imported";

// Matches account-create-groups-panel: dataset groups are ROW_GROUP.
const ROW_GROUP = 1;

/**
 * MAIA wizard — Step 4 of 4 (Group your locations + import).
 *
 * Two views: "grouping" (filter locations, assign to groups until 100%) and
 * "imported" (success summary). Existing groups + create-group are wired to the
 * real GroupService (same API/payload as account-create-groups-panel). Location
 * count and per-group assignment counts are session-local until Import (approve).
 */
@Component({
    selector: "maia-group-step",
    templateUrl: "./maia-group-step.component.html",
    styleUrls: ["./maia-group-step.component.scss"],
})
export class MaiaGroupStepComponent implements OnInit {
    @Input() datasetId: string | null = null;
    @Input() accountId: number | null = null;
    @Input() jobId: number | null = null;
    /** Set on resume when the job's `currentScreen` was already `import_success` — see MaiaPageComponent.applyResumedJob. */
    @Input() initialView: MaiaGroupView | null = null;

    @Output() readonly closeRequested = new EventEmitter<void>();
    @Output() readonly backRequested = new EventEmitter<void>();
    @Output() readonly mapRequested = new EventEmitter<void>();
    @Output() readonly downloadRequested = new EventEmitter<void>();
    /** Fires once every assignment is approved and the success view shows — step 4's completion signal. */
    @Output() readonly imported = new EventEmitter<void>();

    view: MaiaGroupView = "grouping";

    totalLocations = 0;
    // Assignment (gates Import) vs approval (drives the progress bar; advanced by Import).
    groupedLocations = 0;
    approvedLocations = 0;

    // Final import summary, persisted so the "imported" success view survives a refresh
    // (post-import the _ai drafts are gone, so live counts read 0).
    importedLocations = 0;
    importedGroups = 0;
    importedRegions = 0;
    failedGeocodeCount = 0;

    // Recorded assignments applied on Import; import runs the real approval per assignment.
    assignments: PendingAssignment[] = [];
    importing = false;
    importError: string | null = null;

    groups: MaiaGroupRow[] = [];
    loadingGroups = false;

    // Faceted filter (mirrors the existing datapoints filter: pick a field, tick its distinct values).
    fields: SovAiField[] = [];
    selectedFieldId: string | null = null;
    selectedFieldIsNumber = false;
    selectedFieldIsDate = false;
    fieldValueOptions: string[] = [];
    valuePicks: { [value: string]: boolean } = {};
    valueSearch = "";
    rangeMin: number | null = null;
    rangeMax: number | null = null;
    dataMin: number | null = null;
    dataMax: number | null = null;
    /** Date range picker values as yyyy-MM-dd; stored on the criterion as UTC epoch millis like the drafts. */
    dateFrom = "";
    dateTo = "";
    focusedRange: "min" | "max" | null = null;
    loadingValues = false;
    showValuePicker = false;

    criteria: FilterCriterion[] = [];
    matchCount = 0;
    loadingMatch = false;
    selectAll = false;

    selectedGroupId: number | null = null;

    // Inline create-group state (mirrors the existing create-group: name + parent group, ROW_GROUP).
    creatingGroup = false;
    newGroupName = "";
    newGroupParentId: number | null = null;
    savingGroup = false;
    private createThenAdd = false;

    constructor(
        private readonly groupService: GroupService,
        private readonly maiaUploadService: MaiaUploadService
    ) {}

    ngOnInit(): void {
        if (this.initialView) {
            this.view = this.initialView;
        }
        // Pending assignments live only in memory until Import - restore them so a refresh
        // mid-grouping keeps the progress and per-group counts (loadGroups applies the counts).
        this.restoreAssignments();
        this.restoreImportedSummary();
        this.loadGroups();
        this.loadLocationCount();
        this.loadFields();
    }

    private storageKey(): string | null {
        return this.jobId == null ? null : `maia-group-assignments-${this.jobId}`;
    }

    private persistAssignments(): void {
        const key = this.storageKey();
        if (!key) {
            return;
        }
        try {
            localStorage.setItem(key, JSON.stringify(this.assignments));
        } catch {
            // storage unavailable (private mode / blocked) - progress just won't survive refresh
        }
    }

    private restoreAssignments(): void {
        const key = this.storageKey();
        if (!key) {
            return;
        }
        try {
            const raw = localStorage.getItem(key);
            if (!raw) {
                return;
            }
            const saved = JSON.parse(raw) as PendingAssignment[];
            if (!Array.isArray(saved) || !saved.length) {
                return;
            }
            this.assignments = saved;
            this.groupedLocations = saved.reduce((sum, a) => sum + (a.added || 0), 0);
        } catch {
            this.assignments = [];
        }
    }

    private clearAssignments(): void {
        const key = this.storageKey();
        if (!key) {
            return;
        }
        try {
            localStorage.removeItem(key);
        } catch {
            // ignore
        }
    }

    private importedSummaryKey(): string | null {
        return this.jobId == null ? null : `maia-group-imported-${this.jobId}`;
    }

    /** Locations the user imported despite failed geocoding (not-found + geocoder errors) - shown on the success view. */
    private loadFailedGeocodeCount(): void {
        if (this.jobId == null) {
            return;
        }
        this.maiaUploadService.getUploadSummary(this.jobId).subscribe(
            (summary) => {
                this.failedGeocodeCount = summary ? (summary.geocodedNotFound || 0) + (summary.geocodingErrors || 0) : 0;
                this.persistImportedSummary();
            },
            () => {
                // summary unavailable - just omit the failed-geocoding note
            }
        );
    }

    private persistImportedSummary(): void {
        const key = this.importedSummaryKey();
        if (!key) {
            return;
        }
        try {
            localStorage.setItem(key, JSON.stringify({
                locations: this.importedLocations,
                groups: this.importedGroups,
                regions: this.importedRegions,
                failedGeocode: this.failedGeocodeCount,
            }));
        } catch {
            // storage unavailable - summary just won't survive refresh
        }
    }

    private restoreImportedSummary(): void {
        const key = this.importedSummaryKey();
        if (!key) {
            return;
        }
        try {
            const raw = localStorage.getItem(key);
            if (!raw) {
                return;
            }
            const saved = JSON.parse(raw) as { locations: number; groups: number; regions: number; failedGeocode?: number };
            this.importedLocations = saved.locations || 0;
            this.importedGroups = saved.groups || 0;
            this.importedRegions = saved.regions || 0;
            this.failedGeocodeCount = saved.failedGeocode || 0;
        } catch {
            // ignore malformed storage
        }
    }

    /** Re-apply restored assignment counts onto the freshly-loaded group rows (matched by id). */
    private applyAssignmentCounts(): void {
        for (const assignment of this.assignments) {
            const group = this.groups.find((g) => g.id === assignment.groupId);
            if (group) {
                group.count += assignment.added || 0;
            }
        }
    }

    private loadLocationCount(): void {
        if (!this.datasetId) {
            return;
        }
        this.maiaUploadService.getDraftLocationCount(this.datasetId, this.jobId).subscribe((count) => {
            this.totalLocations = count;
        });
    }

    private loadFields(): void {
        if (!this.datasetId) {
            return;
        }
        this.maiaUploadService.getAiFields(this.datasetId).subscribe((fields) => {
            // AI flow lets users filter by every field, high-cardinality included - text fields get a
            // searchable value checklist and number fields a min/max range, so large value sets stay usable.
            this.fields = fields || [];
        });
    }

    loadGroups(): void {
        if (!this.datasetId || this.accountId == null) {
            return;
        }
        this.loadingGroups = true;
        this.groupService.getGroups(this.datasetId, this.accountId).subscribe(
            (groups: Group[]) => {
                this.groups = (groups || []).map((g) => ({
                    id: g.id as number,
                    name: g.name,
                    parentId: g.parentId != null ? g.parentId : null,
                    count: 0,
                }));
                this.applyAssignmentCounts();
                this.loadingGroups = false;
            },
            () => {
                this.loadingGroups = false;
            }
        );
    }

    // ---- faceted filter ----------------------------------------------------
    /** Field picked in the dropdown - number and date fields get a min/max range, others a value checklist. */
    onFieldSelected(): void {
        this.fieldValueOptions = [];
        this.valuePicks = {};
        this.valueSearch = "";
        this.rangeMin = null;
        this.rangeMax = null;
        this.dataMin = null;
        this.dataMax = null;
        this.dateFrom = "";
        this.dateTo = "";
        this.showValuePicker = false;
        if (!this.datasetId || !this.selectedFieldId) {
            return;
        }
        const field = this.fields.find((candidate) => candidate.id === this.selectedFieldId);
        this.selectedFieldIsNumber = !!field && field.type === NUMBER_FIELD;
        this.selectedFieldIsDate = !!field && field.type === DATE_FIELD;
        const existing = this.criteria.find((criterion) => criterion.fieldId === this.selectedFieldId);

        if (this.selectedFieldIsNumber || this.selectedFieldIsDate) {
            if (existing) {
                this.rangeMin = existing.min;
                this.rangeMax = existing.max;
                this.dateFrom = toDateInput(existing.min);
                this.dateTo = toDateInput(existing.max);
            }
            this.showValuePicker = true;
            this.maiaUploadService
                .getFieldRange(this.datasetId, this.selectedFieldId, this.jobId)
                .subscribe((range) => {
                    this.dataMin = range ? range.min : null;
                    this.dataMax = range ? range.max : null;
                });
            return;
        }

        this.loadingValues = true;
        this.maiaUploadService.getFieldValues(this.datasetId, this.selectedFieldId, this.jobId).subscribe(
            (values) => {
                this.fieldValueOptions = (values || []).filter((value) => value != null).sort();
                if (existing) {
                    existing.values.forEach((value) => (this.valuePicks[value] = true));
                }
                this.loadingValues = false;
                this.showValuePicker = true;
            },
            () => {
                this.loadingValues = false;
            }
        );
    }

    get filteredValueOptions(): string[] {
        const term = this.valueSearch.trim().toLowerCase();
        return term
            ? this.fieldValueOptions.filter((value) => value.toLowerCase().includes(term))
            : this.fieldValueOptions;
    }

    private get checkedValues(): string[] {
        return this.fieldValueOptions.filter((value) => this.valuePicks[value]);
    }

    get canApply(): boolean {
        if (this.selectedFieldIsDate) {
            return !!this.dateFrom || !!this.dateTo;
        }
        return this.selectedFieldIsNumber
            ? this.rangeMin != null || this.rangeMax != null
            : this.checkedValues.length > 0;
    }

    /** Native date inputs only open their calendar from the icon; open it from anywhere in the box. */
    openDatePicker(event: MouseEvent): void {
        (event.target as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
    }

    /** Earliest/latest date among the job's drafts, used as the date pickers' bounds. */
    get dataMinDate(): string {
        return toDateInput(this.dataMin);
    }

    get dataMaxDate(): string {
        return toDateInput(this.dataMax);
    }


    /** Turn the ticked values / entered range into a chip (replacing any prior one for the field). */
    applyCriterion(): void {
        const field = this.fields.find((candidate) => candidate.id === this.selectedFieldId);
        if (!field) {
            return;
        }
        this.criteria = this.criteria.filter((criterion) => criterion.fieldId !== field.id);

        if (this.selectedFieldIsDate) {
            if (this.dateFrom || this.dateTo) {
                this.criteria.push(this.dateCriterion(field));
            }
        } else if (this.selectedFieldIsNumber) {
            if (this.rangeMin != null || this.rangeMax != null) {
                this.criteria.push({
                    fieldId: field.id,
                    fieldName: field.displayName || field.name,
                    values: [],
                    min: this.rangeMin,
                    max: this.rangeMax,
                    label: this.rangeLabel(this.rangeMin, this.rangeMax),
                });
            }
        } else {
            const values = this.checkedValues;
            if (values.length > 0) {
                this.criteria.push({
                    fieldId: field.id,
                    fieldName: field.displayName || field.name,
                    values,
                    min: null,
                    max: null,
                    label: values.join(", "),
                });
            }
        }

        this.selectAll = false;
        this.showValuePicker = false;
        this.selectedFieldId = null;
        this.selectedFieldIsNumber = false;
        this.selectedFieldIsDate = false;
        this.dateFrom = "";
        this.dateTo = "";
        this.fieldValueOptions = [];
        this.valuePicks = {};
        this.rangeMin = null;
        this.rangeMax = null;
        this.dataMin = null;
        this.dataMax = null;
        this.refreshMatchCount();
    }

    private rangeLabel(min: number | null, max: number | null): string {
        if (min != null && max != null) {
            return `${min} – ${max}`;
        }
        return min != null ? `≥ ${min}` : `≤ ${max}`;
    }

    private dateCriterion(field: SovAiField): FilterCriterion {
        const from = this.dateFrom;
        const to = this.dateTo;
        let label = `${from} – ${to}`;
        if (!to) {
            label = `on or after ${from}`;
        } else if (!from) {
            label = `on or before ${to}`;
        }
        return {
            fieldId: field.id,
            fieldName: field.displayName || field.name,
            values: [],
            min: from ? Date.parse(`${from}T00:00:00Z`) : null,
            // Inclusive "To": the whole selected day matches, not just its first millisecond.
            max: to ? Date.parse(`${to}T00:00:00Z`) + DAY_MS - 1 : null,
            label,
        };
    }

    removeCriterion(fieldId: string): void {
        this.criteria = this.criteria.filter((criterion) => criterion.fieldId !== fieldId);
        this.refreshMatchCount();
    }

    onSelectAllChange(): void {
        this.refreshMatchCount();
    }

    private refreshMatchCount(): void {
        if (this.selectAll) {
            this.matchCount = this.remainingCount;
            return;
        }
        if (!this.datasetId || this.criteria.length === 0) {
            this.matchCount = 0;
            return;
        }
        this.loadingMatch = true;
        this.maiaUploadService
            .getMatchCount(
                this.datasetId,
                this.jobId,
                this.criteria.map((criterion) => ({
                    fieldId: criterion.fieldId,
                    values: criterion.values,
                    min: criterion.min,
                    max: criterion.max,
                }))
            )
            .subscribe(
                (count) => {
                    this.matchCount = count;
                    this.loadingMatch = false;
                },
                () => {
                    this.loadingMatch = false;
                }
            );
    }

    get remainingCount(): number {
        return this.totalLocations - this.groupedLocations;
    }

    /** Approval progress - the top bar. Advances only when Import runs the approval. */
    get approvalPercent(): number {
        return this.totalLocations ? Math.round((this.approvedLocations / this.totalLocations) * 100) : 0;
    }

    /** Assignment progress - how many locations have been assigned to a group (drives the bar). */
    get groupedPercent(): number {
        return this.totalLocations ? Math.round((this.groupedLocations / this.totalLocations) * 100) : 0;
    }

    /** Every location assigned to a group - the prerequisite that enables Import. */
    get allGrouped(): boolean {
        return this.totalLocations > 0 && this.groupedLocations >= this.totalLocations;
    }

    get canAdd(): boolean {
        return this.matchCount > 0 && this.selectedGroupId != null;
    }

    /** Groups whose subgroups are collapsed (hidden) in the EXISTING GROUPS list. */
    collapsedGroupIds = new Set<number>();

    /** Every group as a depth-first flat list; descendants of collapsed parents are omitted. */
    get displayGroups(): Array<MaiaGroupRow & { isSub: boolean; hasChildren: boolean; depth: number; expanded: boolean }> {
        const rows: Array<MaiaGroupRow & { isSub: boolean; hasChildren: boolean; depth: number; expanded: boolean }> = [];
        const walk = (parentId: number | null, depth: number): void => {
            this.groups
                .filter((group) => (parentId === null ? group.parentId == null : group.parentId === parentId))
                .forEach((group) => {
                    const hasChildren = this.groups.some((child) => child.parentId === group.id);
                    const expanded = !this.collapsedGroupIds.has(group.id);
                    rows.push({ ...group, isSub: depth > 0, hasChildren, depth, expanded });
                    if (expanded) {
                        walk(group.id, depth + 1);
                    }
                });
        };
        walk(null, 0);
        return rows;
    }

    toggleGroup(groupId: number): void {
        if (this.collapsedGroupIds.has(groupId)) {
            this.collapsedGroupIds.delete(groupId);
        } else {
            this.collapsedGroupIds.add(groupId);
        }
    }

    get groupsWithData(): number {
        return this.groups.filter((group) => group.count > 0).length;
    }

    get regionsWithData(): number {
        return this.groups.filter((group) => group.parentId != null && group.count > 0).length;
    }

    addToGroup(): void {
        if (!this.canAdd) {
            return;
        }
        const group = this.groups.find((g) => g.id === this.selectedGroupId);
        if (!group) {
            return;
        }
        const added = Math.min(this.matchCount, this.remainingCount);
        group.count += added;
        this.groupedLocations += added;
        this.assignments.push({
            groupId: group.id,
            selectAll: this.selectAll,
            criteria: this.selectAll ? [] : this.criteria.map((criterion) => ({ ...criterion })),
            added,
        });
        this.persistAssignments();
        this.resetSelection();
    }

    // ---- create group (real API) ------------------------------------------
    openCreateGroup(): void {
        this.creatingGroup = true;
        this.createThenAdd = false;
        this.newGroupName = "";
        this.newGroupParentId = this.rootGroupId();
    }

    openCreateGroupAndAdd(): void {
        this.creatingGroup = true;
        this.createThenAdd = true;
        this.newGroupName = "";
        this.newGroupParentId = this.rootGroupId();
    }

    cancelCreateGroup(): void {
        this.creatingGroup = false;
        this.createThenAdd = false;
        this.newGroupName = "";
        this.newGroupParentId = null;
    }

    confirmCreateGroup(): void {
        const name = this.newGroupName.trim();
        if (!name || this.savingGroup) {
            return;
        }
        const request = { parentID: this.newGroupParentId, name, type: ROW_GROUP };
        this.savingGroup = true;
        this.groupService.createGroup(request, false).subscribe(
            (created: Group) => {
                const row: MaiaGroupRow = {
                    id: created.id as number,
                    name: created.name,
                    parentId: created.parentId != null ? created.parentId : null,
                    count: 0,
                };
                this.groups.push(row);
                this.savingGroup = false;
                const addAfter = this.createThenAdd;
                this.cancelCreateGroup();
                if (addAfter) {
                    this.selectedGroupId = row.id;
                    this.addToGroup();
                }
            },
            () => {
                this.savingGroup = false;
            }
        );
    }

    importPortfolio(): void {
        if (!this.allGrouped || this.importing || !this.datasetId) {
            return;
        }
        this.importing = true;
        this.importError = null;
        this.approvedLocations = 0;
        // Specific-criteria assignments first; "select all remaining" last so it only grabs leftovers.
        const ordered = [...this.assignments].sort(
            (a, b) => Number(a.selectAll) - Number(b.selectAll)
        );
        this.runAssignment(ordered, 0);
    }

    /** Approves each recorded assignment in turn, advancing the bar; success only once all complete. */
    private runAssignment(ordered: PendingAssignment[], index: number): void {
        if (index >= ordered.length) {
            this.importing = false;
            this.importedLocations = this.approvedLocations || this.groupedLocations || this.totalLocations;
            this.importedGroups = this.groupsWithData;
            this.importedRegions = this.regionsWithData;
            this.persistImportedSummary();
            this.loadFailedGeocodeCount();
            this.selectImportedGroupsOnMap();
            this.clearAssignments(); // pending grouping consumed by the real approval
            this.view = "imported";
            this.imported.emit();
            return;
        }
        const assignment = ordered[index];
        const criteria = assignment.criteria.map((criterion) => ({
            fieldId: criterion.fieldId,
            values: criterion.values,
            min: criterion.min,
            max: criterion.max,
        }));
        this.maiaUploadService.approve(this.datasetId as string, this.jobId, assignment.groupId, criteria).subscribe(
            (result) => {
                this.approvedLocations += result.approved;
                this.runAssignment(ordered, index + 1);
            },
            () => {
                this.importing = false;
                this.importError = "Import failed. Please try again.";
            }
        );
    }

    /**
     * The map restores its group filter from `selectedGroups_<accountId>`, and a group missing from
     * that list - as one created here always is - comes back unticked and deselects its parent too,
     * so "Go to Map View" opens on an empty map. Add the groups that just received locations. With
     * nothing saved the map runs its default selection instead, so that case is left alone.
     */
    private selectImportedGroupsOnMap(): void {
        if (this.accountId == null) {
            return;
        }
        const key = getGroupsLocalStorageKey(this.accountId);
        try {
            const saved = JSON.parse(localStorage.getItem(key) || "[]");
            if (!Array.isArray(saved) || !saved.length) {
                return;
            }
            const groupIds = new Set<number>([...saved, ...this.assignments.map((a) => a.groupId)]);
            localStorage.setItem(key, JSON.stringify([...groupIds]));
        } catch {
            // storage unavailable or malformed - the map falls back to its own selection
        }
    }

    /** New dataset groups are created under the dataset's root group, like the existing panel. */
    private rootGroupId(): number | null {
        const root = this.groups.find((group) => group.parentId == null);
        return root ? root.id : null;
    }

    private resetSelection(): void {
        this.criteria = [];
        this.matchCount = 0;
        this.selectAll = false;
        this.selectedGroupId = null;
        this.selectedFieldId = null;
        this.selectedFieldIsNumber = false;
        this.selectedFieldIsDate = false;
        this.dateFrom = "";
        this.dateTo = "";
        this.fieldValueOptions = [];
        this.valuePicks = {};
        this.valueSearch = "";
        this.rangeMin = null;
        this.rangeMax = null;
        this.dataMin = null;
        this.dataMax = null;
        this.showValuePicker = false;
    }
}
